"use client";

import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import { jwtDecode } from "jwt-decode";
import * as XLSX from "xlsx";
import Papa from "papaparse";
import { TrashIcon } from "@heroicons/react/20/solid";
import PizZip from "pizzip";
import Docxtemplater from "docxtemplater";
import ImageModule from "docxtemplater-image-module-free";
import { saveAs } from "file-saver";
import AlertDialog from "./AlertDialog";
import CreatableSelect from "react-select/creatable"; // Replace Select with CreatableSelect
const customStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#4b5563",
    borderColor: state.isFocused ? "#f97316" : "#6b7280",
    color: "#e5e7eb",
    boxShadow: state.isFocused ? "0 0 0 1px #f97316" : "none",
    "&:hover": { borderColor: "#f97316" },
    minHeight: "38px",
    height: "38px",
    fontSize: "12px",
  }),
  input: (provided) => ({
    ...provided,
    color: "#e5e7eb",
    outline: "none",
    boxShadow: "none",
    border: "none",
    margin: 0,
    padding: 0,
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: "0 4px",
  }),
  singleValue: (provided) => ({
    ...provided,
    fontSize: "12px",
    color: "#e5e7eb",
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    padding: "0",
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    padding: "2px",
    color: "#9ca3af",
    "&:hover": { color: "#e5e7eb" },
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? "#4b5563" : "#374151",
    color: state.isSelected ? "#f97316" : "#e5e7eb",
    "&:hover": { backgroundColor: "#6b7280" },
    fontSize: "12px",
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "#374151",
    borderRadius: "0.375rem",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
    border: "1px solid #4b5563",
    zIndex: 9999,
    maxHeight: "200px",
    overflowY: "auto",
    position: "absolute",
    width: "100%",
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: "200px",
    overflowY: "auto",
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 9999, // High z-index for overlapping
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: "#4b5563",
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: "#e5e7eb",
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: "#e5e7eb",
    "&:hover": {
      backgroundColor: "#f97316",
      color: "#fff",
    },
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#9ca3af",
  }),
};

export default function CreateChangeOrderForm({
  budgetItems: initialBudgetItems = [],
  editId = null,
}) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: "",
    termsAndConditions: "Original terms and conditions.",
    approvalStatus: "Pending",
    overheadPercentage: 0,
    changesToSchedule: 0,
  });
  const [selectedMaterials, setSelectedMaterials] = useState([]);
  const [laborItems, setLaborItems] = useState([]);
  const [surchargeItems, setSurchargeItems] = useState([]);
  const [uploadedItems, setUploadedItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [costCodes, setCostCodes] = useState([]);
  const [project, setProject] = useState(null);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [newLaborItem, setNewLaborItem] = useState({
    labourTitle: "",
    quantity: 0,
    labourRate: 0,
    labourPrice: 0,
    uom: "",
    costCode: "",
    customer: true,
    budget: true,
  });
  const [newMaterialItem, setNewMaterialItem] = useState({
    title: "",
    quantity: 0,
    unitCost: 0,
    price: 0,
    vendor: null,
    costCode: "",
    bidArea: "",
    itemCode: "",
    itemDescription: "",
    size: "",
    container: "",
    customer: true,
    budget: true,
    sectionNumber: "",
  });
  const [newSurchargeItem, setNewSurchargeItem] = useState({
    title: "",
    quantity: 0,
    unitCost: 0,
    unitPrice: 0,
    costCode: "",
    customer: true,
    budget: true,
  });
  const [summary, setSummary] = useState({
    description: "",
    qtyUnit: "1",
    totalPriceForCustomer: 0,
    totalCost: 0,
    markup: 0,
    billable: false,
  });
  const [availableLabourItems, setAvailableLabourItems] = useState([]);
  const [availableMaterialItems, setAvailableMaterialItems] = useState([]); // New state for material items
  const [selectedLabourOption, setSelectedLabourOption] = useState(null);
  const [selectedMaterialOption, setSelectedMaterialOption] = useState(null); // New state for selected material
  const [savedChangeOrder, setSavedChangeOrder] = useState(null);
  const [showBudgetUpload, setShowBudgetUpload] = useState(false);
  const [showMaterialInput, setShowMaterialInput] = useState(false); // New state for toggling material input
  const [budgetFile, setBudgetFile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [alert, setAlert] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
    onConfirm: () => setAlert({ open: false }),
  });
  const [bidAreaOptions, setBidAreaOptions] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const token = localStorage.getItem("token");
        const requests = [
          axios.get(
            `https://erp-production-72da01c8e651.herokuapp.com/api/projects/${id}`,
            { headers: { Authorization: `Bearer ${token}` } },
          ),
          axios.get(
            "https://erp-production-72da01c8e651.herokuapp.com/api/accounts/vendors",
            { headers: { Authorization: `Bearer ${token}` } },
          ),
          axios.get(
            `https://erp-production-72da01c8e651.herokuapp.com/api/company-profile`,
            { headers: { Authorization: `Bearer ${token}` } },
          ),
          axios.get(
            "https://erp-production-72da01c8e651.herokuapp.com/api/costCodes",
            { headers: { Authorization: `Bearer ${token}` } },
          ),
        ];
        if (editId) {
          requests.push(
            axios.get(
              `https://erp-production-72da01c8e651.herokuapp.com/api/changeOrders/${editId}`,
              { headers: { Authorization: `Bearer ${token}` } },
            ),
          );
        }
        const responses = await Promise.all(requests);
        const [
          projectResponse,
          vendorsResponse,
          companyProfileResponse,
          costCodesResponse,
          changeOrderResponse,
        ] = editId ? responses : [...responses, null];
        setProject(projectResponse.data);
        setVendors(vendorsResponse.data);
        setCostCodes(costCodesResponse.data);

        // Process labor items from contractLabourItems
        const contractLaborOptions = projectResponse.data.contractLabourItems
          .filter((item) => item && item.labourItem)
          .map((item) => {
            let costCode = item.costCode || null;
            const sectionNumber = item.sectionNumber || "";
            if (!costCode && sectionNumber) {
              const sectionNumberLower = sectionNumber.trim().toLowerCase();
              const matchingCostCode = costCodesResponse.data.find((cc) =>
                cc.code?.trim().toLowerCase().includes(sectionNumberLower),
              );
              costCode = matchingCostCode ? matchingCostCode._id : null;
            }
            return {
              value: item._id,
              label: `${item.labourItem?.title || "Unnamed"} (Rate: $${(
                item.unitRate || 0
              ).toFixed(2)} / Cost: $${(item.unitCost || 0).toFixed(2)} / ${
                item.uom || "N/A"
              })`,
              unitRate: item.unitRate || 0,
              unitCost: item.unitCost || 0,
              uom: item.uom || "",
              itemCode: item.itemCode || "",
              itemDescription: item.labourItem?.title || "Unnamed",
              bidArea: item.bidArea || "",
              costCode,
              sectionNumber,
              source: "contract",
            };
          });

        // Process labor items from budgets (category "L")
        const budgetLaborOptions = Object.values(
          projectResponse.data.budgets
            .filter((budget) => !budget.isVariance && !budget.isChangeOrder)
            .flatMap((budget) => budget.lineItems || [])
            .filter((item) => item.category === "L")
            .reduce((acc, item) => {
              const key = `${
                item.itemDescription || item.costCodeDescription || "Unnamed"
              }|${item.uom || ""}`;
              if (
                !acc[key] ||
                item.unitPrice > acc[key].unitPrice ||
                (item.unitPrice === acc[key].unitPrice &&
                  item.itemCode > acc[key].itemCode)
              ) {
                let costCode = item.costCode || null;
                if (!costCode && item.sectionNumber) {
                  const sectionNumber = item.sectionNumber.trim().toLowerCase();
                  const matchingCostCode = costCodesResponse.data.find((cc) =>
                    cc.code.trim().toLowerCase().includes(sectionNumber),
                  );
                  costCode = matchingCostCode ? matchingCostCode._id : null;
                }
                acc[key] = {
                  value: item._id,
                  label: `${
                    item.itemDescription ||
                    item.costCodeDescription ||
                    "Unnamed"
                  } (Rate: $${(item.unitRate || item.unitPrice || 0).toFixed(
                    2,
                  )} / ${item.uom || "N/A"})`,
                  unitRate: item.unitRate || item.price || 0,
                  unitCost: item.unitRate || item.cost || 0,
                  uom: item.uom || "",
                  itemCode: item.itemCode || "",
                  itemDescription:
                    item.itemDescription ||
                    item.costCodeDescription ||
                    "Unnamed",
                  bidArea: item.bidArea || "",
                  costCode,
                  sectionNumber: item.sectionNumber || "",
                  source: "budget",
                };
              }
              return acc;
            }, {}),
        );

        // Combine contract and budget labor items, avoiding duplicates
        const combinedLaborOptions = [
          ...contractLaborOptions,
          ...budgetLaborOptions.filter(
            (budgetItem) =>
              !contractLaborOptions.some(
                (contractItem) =>
                  contractItem.itemDescription === budgetItem.itemDescription &&
                  contractItem.uom === budgetItem.uom,
              ),
          ),
        ];
        setAvailableLabourItems(combinedLaborOptions);

        // Process material items from budgets (category "M")
        setAvailableMaterialItems(
          Object.values(
            projectResponse.data.budgets
              .filter((budget) => !budget.isVariance && !budget.isChangeOrder)
              .flatMap((budget) => budget.lineItems || [])
              .filter((item) => item.category === "M")
              .reduce((acc, item) => {
                const key = `${item.itemDescription || "Unnamed"}|${
                  item.size || ""
                }`;
                if (
                  !acc[key] ||
                  item.price > acc[key].price ||
                  (item.price === acc[key].price &&
                    item.itemCode > acc[key].itemCode)
                ) {
                  let costCode = item.costCode || null;
                  if (!costCode && item.sectionNumber) {
                    const sectionNumber = item.sectionNumber
                      .trim()
                      .toLowerCase();
                    const matchingCostCode = costCodesResponse.data.find((cc) =>
                      cc.code.trim().toLowerCase().includes(sectionNumber),
                    );
                    costCode = matchingCostCode ? matchingCostCode._id : null;
                  }
                  acc[key] = {
                    value: item._id,
                    label: `${
                      item.itemDescription || "Unnamed"
                    } (Price: $${item.price.toFixed(
                      2,
                    )} / Cost: $${item.cost.toFixed(2)} - Size: ${item.size})`,
                    price: item.price,
                    cost: item.cost,
                    itemCode: item.itemCode || "",
                    itemDescription: item.itemDescription || "",
                    size: item.size || "",
                    container: item.container || "",
                    bidArea: item.bidArea || "",
                    vendor: item.vendor?._id || item.vendor || null,
                    costCode,
                    sectionNumber: item.sectionNumber || "",
                  };
                }
                return acc;
              }, {}),
          ),
        );

        const uniqueBidAreas = [
          ...new Set(
            projectResponse.data.budgets
              .filter((budget) => !budget.isVariance && !budget.isChangeOrder)
              .flatMap((budget) => budget.lineItems || [])
              .filter((item) => item.bidArea)
              .map((item) => item.bidArea),
          ),
        ].map((bidArea) => ({ value: bidArea, label: bidArea }));
        setBidAreaOptions(uniqueBidAreas);

        if (
          !companyProfileResponse.data ||
          Object.keys(companyProfileResponse.data).length === 0
        ) {
          setAlert({
            open: true,
            type: "error",
            title: "Error",
            message:
              "Company profile is not set. Please configure it in the settings.",
            onConfirm: () => setAlert({ ...alert, open: false }),
          });
          setCompanyProfile(null);
        } else {
          setCompanyProfile(companyProfileResponse.data);
        }

        if (changeOrderResponse) {
          const changeOrder = changeOrderResponse.data;
          setFormData({
            title: changeOrder.title,
            termsAndConditions: changeOrder.termsAndConditions,
            approvalStatus: changeOrder.approvalStatus,
            overheadPercentage: changeOrder.overheadPercentage,
            changesToSchedule: changeOrder.changesToSchedule,
          });
          setSelectedMaterials(
            changeOrder.items
              .filter((item) => item.type === "material")
              .map((item) => ({
                ...item,
                uploadId: `material-${item._id || Date.now()}`,
                customer: item.customer !== undefined ? item.customer : true,
                budget: item.budget !== undefined ? item.budget : true,
              })),
          );
          setLaborItems(
            changeOrder.items
              .filter((item) => item.type === "labor" && item.category !== "S")
              .map((item) => ({
                ...item,
                uploadId: `labor-${item._id || Date.now()}`,
                labourTitle: item.title,
                labourRate: item.unitCost,
                labourPrice: item.price,
                customer: item.customer !== undefined ? item.customer : true,
                budget: item.budget !== undefined ? item.budget : true,
              })),
          );
          setSurchargeItems(
            changeOrder.items
              .filter((item) => item.type === "labor" && item.category === "S")
              .map((item) => ({
                ...item,
                uploadId: `surcharge-${item._id || Date.now()}`,
                title: item.title,
                unitCost: item.unitCost,
                price: item.price,
                customer: item.customer !== undefined ? item.customer : true,
                budget: item.budget !== undefined ? item.budget : true,
              })),
          );
          setUploadedItems(
            changeOrder.items.map((item) => ({
              ...item,
              uploadId: `${item.type}-${item._id || Date.now()}`,
              labourTitle:
                item.type === "labor" && item.category !== "S"
                  ? item.title
                  : undefined,
              labourRate:
                item.type === "labor" && item.category !== "S"
                  ? item.unitCost
                  : undefined,
              labourPrice:
                item.type === "labor" && item.category !== "S"
                  ? item.price
                  : undefined,
              ...(item.category === "S" && {
                title: item.title,
                unitCost: item.unitCost,
                unitPrice: item.price,
              }),
              customer: item.customer !== undefined ? item.customer : true,
              budget: item.budget !== undefined ? item.budget : true,
            })),
          );
          setSummary({
            description: changeOrder.summary.description,
            qtyUnit: changeOrder.summary.qtyUnit,
            billable: changeOrder.summary.billable,
            totalPriceForCustomer: changeOrder.totalPrice,
            totalCost: changeOrder.totalCost,
            markup:
              changeOrder.totalCost > 0
                ? ((changeOrder.totalPrice - changeOrder.totalCost) /
                    changeOrder.totalCost) *
                  100
                : 0,
          });
          setSavedChangeOrder(changeOrder);
        }
      } catch (err) {
        setAlert({
          open: true,
          type: "error",
          title: "Error",
          message:
            "Failed to load data: " +
            (err.response?.data?.message || err.message),
          onConfirm: () => setAlert({ ...alert, open: false }),
        });
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [id, editId]);

  const vendorOptions = vendors.map((vendor) => ({
    value: vendor._id,
    label: vendor.companyName,
  }));

  const costCodeOptions = costCodes.map((costCode) => ({
    value: costCode._id,
    label: `${costCode.code} - ${costCode.description}`,
  }));

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        name === "overheadPercentage" || name === "changesToSchedule"
          ? parseFloat(value) || 0
          : value,
    }));
  };

  const handleNewMaterialChange = (e) => {
    const { name, value } = e.target;

    setNewMaterialItem((prev) => {
      // Base update for the changed field
      let updates = {
        [name]:
          name === "quantity"
            ? Math.ceil(parseFloat(value) || 0)
            : name === "unitCost" || name === "price"
              ? Number(parseFloat(value).toFixed(2)) || 0
              : value,
      };

      // IMPORTANT: Always sync description when title changes
      // (this overrides any previous description value)
      if (name === "title") {
        updates.itemDescription = value.trim(); // live mirror
      }

      return {
        ...prev,
        ...updates,
      };
    });
  };
  // New handler for material item selection
  const handleMaterialItemSelect = (selectedOption) => {
    setSelectedMaterialOption(selectedOption);

    if (selectedOption) {
      let costCode = selectedOption.costCode || null;
      if (!costCode && selectedOption.sectionNumber) {
        const sectionNumber = selectedOption.sectionNumber.trim().toLowerCase();
        const matchingCostCode = costCodes.find((cc) =>
          cc.code?.trim().toLowerCase().includes(sectionNumber),
        );
        costCode = matchingCostCode ? matchingCostCode._id : null;
      }

      // Debug helper: log what we're actually getting
      console.log("Selected material option:", {
        fullLabel: selectedOption.label,
        itemCode: selectedOption.itemCode,
        itemDescription: selectedOption.itemDescription,
      });

      setNewMaterialItem((prev) => ({
        ...prev,
        title: selectedOption.label?.split(" (")[0] || "", // clean title
        unitCost: selectedOption.cost || 0,
        price: selectedOption.price || 0,
        // Use itemCode directly — this is the actual code (should NOT be the title)
        itemCode: selectedOption.itemCode || "",
        // Description can stay as-is or fallback to title if missing
        itemDescription:
          selectedOption.itemDescription?.trim() ||
          selectedOption.label?.split(" (")[0]?.trim() ||
          "",
        size: selectedOption.size || "",
        container: selectedOption.container || "",
        bidArea: selectedOption.bidArea || "",
        vendor: selectedOption.vendor || null,
        costCode,
        sectionNumber: selectedOption.sectionNumber || "",
      }));
    } else {
      setNewMaterialItem({
        title: "",
        quantity: 0,
        unitCost: 0,
        price: 0,
        vendor: null,
        costCode: "",
        bidArea: "",
        itemCode: "",
        itemDescription: "",
        size: "",
        container: "",
        customer: true,
        budget: true,
        sectionNumber: "",
      });
    }
  };

  // New handler for adding material items
  const handleAddMaterialItem = () => {
    const errors = [];

    if (!newMaterialItem.title?.trim()) {
      errors.push("Title is required");
    }
    if (!newMaterialItem.size?.trim()) {
      errors.push("Size / Style is required");
    }
    if (!newMaterialItem.costCode) {
      errors.push("Cost Code is required");
    }
    if (!newMaterialItem.bidArea?.trim()) {
      errors.push("Bid Area is required");
    }
    if (!newMaterialItem.container?.trim()) {
      errors.push("Container is required");
    }
    if (!newMaterialItem.vendor) {
      errors.push("Vendor is required");
    }
    if (newMaterialItem.unitCost <= 0) {
      errors.push("Unit Cost must be greater than 0");
    }
    if (newMaterialItem.price <= 0) {
      errors.push("Unit Price must be greater than 0");
    }

    if (errors.length > 0) {
      setAlert({
        open: true,
        type: "error",
        title: "Missing Required Fields",
        message: errors.join(" • "),
      });
      return;
    }

    const newItem = {
      uploadId: `material-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      title: newMaterialItem.title.trim(),
      quantity: newMaterialItem.quantity,
      unitCost: newMaterialItem.unitCost,
      price: newMaterialItem.price,
      type: "material",
      category: "M",
      costCodeDescription: newMaterialItem.title.trim(),
      amount: Number(
        (newMaterialItem.quantity * newMaterialItem.unitCost).toFixed(2),
      ),
      itemCode: newMaterialItem.itemCode?.trim() || "", // optional
      itemDescription:
        newMaterialItem.itemDescription?.trim() || newMaterialItem.title.trim(),
      size: newMaterialItem.size.trim(),
      container: newMaterialItem.container?.trim() || "",
      bidArea: newMaterialItem.bidArea.trim(),
      vendor: newMaterialItem.vendor,
      costCode: newMaterialItem.costCode,
      sectionNumber:
        newMaterialItem.sectionNumber ||
        costCodes
          .find((cc) => cc._id === newMaterialItem.costCode)
          ?.code.split(" - ")[0]
          ?.trim() ||
        "",
      customer: newMaterialItem.customer,
      budget: newMaterialItem.budget,
      requiredQty: newMaterialItem.quantity,
    };

    setUploadedItems((prev) => [...prev, newItem]);
    setSelectedMaterials((prev) => [...prev, newItem]); // if you're still using this array

    // Reset form
    setNewMaterialItem({
      title: "",
      quantity: 0,
      unitCost: 0,
      price: 0,
      vendor: null,
      costCode: "",
      bidArea: "",
      itemCode: "",
      itemDescription: "",
      size: "",
      container: "",
      customer: true,
      budget: true,
    });

    setSelectedMaterialOption(null);
    setShowMaterialInput(false);

    setAlert({
      open: true,
      type: "success",
      title: "Added",
      message: "Material line item added successfully",
    });
  };

  const handleBudgetFileUpload = (e) => {
    e.preventDefault();
    if (!budgetFile) {
      setAlert({
        open: true,
        type: "error",
        title: "Upload Error",
        message: "Please select a change order budget file.",
      });
      return;
    }
    const fileExtension = budgetFile.name.split(".").pop().toLowerCase();
    const processFile = async (data) => {
      const cleanNumber = (value) => {
        if (value == null) return 0;
        const str = String(value).trim();
        const cleaned = str.replace(/[$,]/g, "");
        return parseFloat(cleaned) || 0;
      };
      // Extract bid areas from the file
      const fileBidAreas = [
        ...new Set(
          data
            .filter((row) => row["Bid Area"]?.trim())
            .map((row) => row["Bid Area"].trim()),
        ),
      ].map((bidArea) => ({ value: bidArea, label: bidArea }));

      // Update bidAreaOptions with new bid areas from the file
      setBidAreaOptions((prev) =>
        [
          ...new Set([
            ...prev.map((opt) => opt.value),
            ...fileBidAreas.map((opt) => opt.value),
          ]),
        ].map((value) => ({ value, label: value })),
      ); // Fixed: Convert Set to array before map

      const parsedItems = await Promise.all(
        data.map(async (row, index) => {
          if (!row || typeof row !== "object") {
            console.warn(`Row ${index} is invalid or empty:`, row);
            return null;
          }
          let vendorId = null;
          const category = row["Category"]?.trim();
          const amount = cleanNumber(row["Amount"]) || 0;
          const laborQTY = cleanNumber(row["Quantity"]) || 0;
          const sectionNumber = row["Section #"]?.trim() || "";
          const matchingCostCode = costCodes.find((cc) =>
            sectionNumber ? cc.code.includes(sectionNumber) : false,
          );
          const sellingPrice = cleanNumber(
            row["Customer Price"] ||
              row["Customer price"] ||
              row[" Price "] ||
              row["Price"],
          );
          const costCodeId = matchingCostCode
            ? matchingCostCode._id
            : costCodes[0]?._id;
          const bidArea = row["Bid Area"]?.trim() || ""; // Use bidArea directly from file

          if (category === "M") {
            const cost = cleanNumber(row["Price"]) || 0;
            const materialSize = row["Size"] || "";
            const requiredQty = cleanNumber(row["Required Qty"]) || 0;
            const itemCode = row["Item Code"] || "";
            let price = sellingPrice;
            let unitCost = cost;
            if (requiredQty >= 0 && itemCode && project?.budgets?.length > 0) {
              const normalizeSize = (size) => {
                if (!size) return "";
                return size
                  .toLowerCase()
                  .replace(/['"gaftin]+/g, "")
                  .replace(/\s*\(\s*|\s*\)\s*/g, "")
                  .replace(/\s+/g, "")
                  .replace(/fine\s*thread|ss|o\.c/gi, "")
                  .trim();
              };
              const matchingItems = project.budgets
                .filter((budget) => !budget.isVariance && !budget.isChangeOrder)
                .flatMap((budget) => budget.lineItems)
                .filter(
                  (lineItem) =>
                    lineItem.category === "M" &&
                    lineItem.itemCode === itemCode &&
                    lineItem.price > 0 &&
                    lineItem.cost > 0 &&
                    normalizeSize(lineItem.size) ===
                      normalizeSize(materialSize),
                );
              if (matchingItems.length > 0) {
                price = Math.max(...matchingItems.map((item) => item.price));
                unitCost = Math.max(...matchingItems.map((item) => item.cost));
              }
            }
            if (row["Part Number"]?.trim()) {
              const vendorName = row["Part Number"].trim();
              const matchedVendors = vendors.filter((v) =>
                v.companyName.toLowerCase().includes(vendorName.toLowerCase()),
              );
              if (matchedVendors.length === 1) {
                vendorId = matchedVendors[0]._id;
              } else if (matchedVendors.length > 1) {
                console.warn(
                  `Multiple vendors found for '${vendorName}' at row ${index}, using first match`,
                );
                vendorId = matchedVendors[0]._id;
              }
            }
            if (row["Vendor"]?.trim()) {
              const vendorName = row["Vendor"].trim();
              const matchedVendors = vendors.filter((v) =>
                v.companyName.toLowerCase().includes(vendorName.toLowerCase()),
              );
              if (matchedVendors.length === 1) {
                vendorId = matchedVendors[0]._id;
              } else if (matchedVendors.length > 1) {
                console.warn(
                  `Multiple vendors found for '${vendorName}' at row ${index}, using first match`,
                );
                vendorId = matchedVendors[0]._id;
              }
            }
            let quantity =
              requiredQty === 0 && price > 0 && amount !== 0
                ? Math.ceil(amount / price)
                : Math.ceil(requiredQty || 1);
            const computedAmount =
              requiredQty < 0
                ? amount
                : Number((quantity * unitCost).toFixed(2));
            return {
              uploadId: `uploaded-${index}`,
              sectionNumber,
              category: "M",
              costCodeDescription: row["Cost Code Description"] || "",
              amount: computedAmount,
              title:
                row["Item Description"] || row["Cost Code Description"] || "",
              quantity,
              unitCost: Number(unitCost.toFixed(2)),
              price: Number(price.toFixed(2)),
              cost: Number(unitCost.toFixed(2)),
              type: "material",
              itemCode,
              itemDescription: row["Item Description"] || "",
              size: row["Size"] || "",
              requiredQty: quantity,
              container: row["Container"] || "",
              bidArea, // Use bidArea from file
              vendor: vendorId,
              costCode: costCodeId,
              customer: true,
              budget: true,
            };
          } else if (category === "L") {
            const computedQuantity = laborQTY !== 0 ? laborQTY : 1;
            const unitCost =
              amount !== 0 && laborQTY !== 0
                ? Number((amount / laborQTY).toFixed(2))
                : 0;
            const computedAmount =
              laborQTY < 0 ? amount : Number((laborQTY * unitCost).toFixed(2));
            return {
              uploadId: `uploaded-${index}`,
              labourTitle: row["Cost Code Description"] || "",
              quantity: computedQuantity,
              unitCost,
              unitPrice: unitCost,
              type: "labor",
              category: "L",
              costCodeDescription: row["Cost Code Description"] || "",
              amount: computedAmount,
              uom: row["UOM"] || "",
              laborHours: cleanNumber(row["Labor Hours"]) || 0,
              bidArea, // Use bidArea from file
              costCode: costCodeId,
              sectionNumber,
              customer: true,
              budget: true,
            };
          } else if (category === "S") {
            const computedQuantity = laborQTY !== 0 ? laborQTY : 1;
            const unitCost = cleanNumber(row["Unit Cost"]) || 0;
            const unitPrice = cleanNumber(row["Unit Price"]) || unitCost;
            const computedAmount =
              computedQuantity < 0
                ? amount
                : Number((computedQuantity * unitCost).toFixed(2));
            return {
              uploadId: `surcharge-${index}`,
              title:
                row["Cost Code Description"] || row["Item Description"] || "",
              quantity: computedQuantity,
              unitCost: Number(unitCost.toFixed(2)),
              unitPrice: Number(unitPrice.toFixed(2)),
              price: Number(unitPrice.toFixed(2)),
              type: "labor",
              category: "S",
              costCodeDescription: row["Cost Code Description"] || "",
              amount: computedAmount,
              uom: "N/A",
              laborHours: 0,
              bidArea: "", // Surcharges typically don't have bid areas
              costCode: costCodeId,
              sectionNumber,
              customer: true,
              budget: true,
            };
          } else {
            console.warn(`Row ${index} has invalid or missing Category:`, row);
            return null;
          }
        }),
      );
      const filteredItems = parsedItems.filter(
        (item) => item != null && item.type != null,
      );
      if (filteredItems.length === 0) {
        setAlert({
          open: true,
          type: "error",
          title: "Error",
          message:
            "No valid items found in the uploaded file. Please check the file format and data.",
        });
        return;
      }
      setUploadedItems((prev) => [...prev, ...filteredItems]);
      setLaborItems((prev) => [
        ...prev,
        ...filteredItems.filter(
          (item) => item.type === "labor" && item.category !== "S",
        ),
      ]);
      setSurchargeItems((prev) => [
        ...prev,
        ...filteredItems.filter(
          (item) => item.type === "labor" && item.category === "S",
        ),
      ]);
      setSelectedMaterials((prev) => [
        ...prev,
        ...filteredItems.filter((item) => item.type === "material"),
      ]);
      setBudgetFile(null);
      setShowBudgetUpload(false);
      setAlert({
        open: true,
        type: "success",
        title: "Upload Success",
        message:
          "Budget uploaded successfully! Click 'Save Change Order' to save.",
      });
    };
    if (fileExtension === "csv") {
      Papa.parse(budgetFile, {
        header: true,
        skipEmptyLines: true,
        complete: (result) => {
          if (!result.data || result.data.length === 0) {
            setAlert({
              open: true,
              type: "error",
              title: "Error",
              message: "The CSV file is empty or invalid.",
            });
            return;
          }
          processFile(result.data);
        },
        error: (err) =>
          setAlert({
            open: true,
            type: "error",
            title: "Parse Error",
            message: "Error parsing CSV file: " + err.message,
          }),
      });
    } else if (fileExtension === "xlsx") {
      const reader = new FileReader();
      reader.onload = (event) => {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);
        if (!jsonData || jsonData.length === 0) {
          setAlert({
            open: true,
            type: "error",
            title: "Error",
            message: "The Excel file is empty or invalid.",
          });
          return;
        }
        processFile(jsonData);
      };
      reader.onerror = () =>
        setAlert({
          open: true,
          type: "error",
          title: "Read Error",
          message: "Error reading XLSX file.",
        });
      reader.readAsArrayBuffer(budgetFile);
    } else {
      setAlert({
        open: true,
        type: "error",
        title: "Format Error",
        message: "Unsupported file format. Please upload a .csv or .xlsx file.",
      });
    }
  };

  const handleMaterialSelect = (itemId) => {
    const item = uploadedItems.find((i) => i.uploadId === itemId);
    if (!item) return;
    setSelectedMaterials((prev) => {
      const isSelected = prev.some((m) => m.uploadId === itemId);
      if (isSelected) {
        return prev.filter((m) => m.uploadId !== itemId);
      } else {
        return [
          ...prev,
          { ...item, customer: item.customer, budget: item.budget },
        ];
      }
    });
  };

  const handleMaterialChange = (index, field, value) => {
    setSelectedMaterials((prev) => {
      const updated = [...prev];
      const newValue =
        field === "quantity" ||
        field === "bidArea" ||
        field === "vendor" ||
        field === "costCode" ||
        field === "customer" ||
        field === "budget"
          ? field === "quantity"
            ? Math.ceil(parseFloat(value) || 0)
            : field === "customer" || field === "budget"
              ? value
              : value
          : Number(parseFloat(value).toFixed(2)) || 0;
      updated[index] = {
        ...updated[index],
        [field]: newValue,
        ...(field === "quantity" && {
          amount: Number((newValue * updated[index].unitCost).toFixed(2)),
          requiredQty: newValue,
        }),
        ...(field === "unitCost" && {
          amount: Number((updated[index].quantity * newValue).toFixed(2)),
        }),
        ...(field === "price" && { price: newValue }),
      };
      // Sync with uploadedItems
      setUploadedItems((prevItems) =>
        prevItems.map((item) =>
          item.uploadId === updated[index].uploadId
            ? { ...item, ...updated[index] }
            : item,
        ),
      );
      return updated;
    });
  };
  const handleLaborChange = (e) => {
    const { name, value } = e.target;
    setNewLaborItem((prev) => ({
      ...prev,
      [name]:
        name === "quantity"
          ? Math.ceil(parseFloat(value) || 0)
          : name === "unitCost" || name === "unitPrice"
            ? Number(parseFloat(value).toFixed(2)) || 0
            : value,
    }));
  };

  const handleSurchargeChange = (e) => {
    const { name, value } = e.target;
    setNewSurchargeItem((prev) => ({
      ...prev,
      [name]:
        name === "quantity"
          ? Math.ceil(parseFloat(value) || 0)
          : name === "unitCost" || name === "unitPrice"
            ? Number(parseFloat(value).toFixed(2)) || 0
            : value,
    }));
  };

  const handleLaborItemChange = (index, field, value) => {
    setLaborItems((prev) => {
      const updated = [...prev];
      const newValue =
        field === "quantity" ||
        field === "bidArea" ||
        field === "costCode" ||
        field === "customer" ||
        field === "budget"
          ? field === "quantity"
            ? Math.ceil(parseFloat(value) || 0)
            : field === "customer" || field === "budget"
              ? value
              : value
          : Number(parseFloat(value).toFixed(2)) || 0;
      updated[index] = {
        ...updated[index],
        [field === "labourTitle" ? "labourTitle" : field]: newValue,
        ...(field === "labourTitle" && { costCodeDescription: value }),
        amount:
          field === "quantity" || field === "unitCost"
            ? Number(
                (
                  (field === "quantity" ? newValue : updated[index].quantity) *
                  (field === "unitCost" ? newValue : updated[index].unitCost)
                ).toFixed(2),
              )
            : updated[index].amount,
        ...(field === "unitPrice" && { unitPrice: newValue }),
      };
      // Update uploadedItems to keep in sync
      setUploadedItems((prevItems) =>
        prevItems.map((item) =>
          item.uploadId === updated[index].uploadId
            ? { ...item, ...updated[index] }
            : item,
        ),
      );
      return updated;
    });
  };
  const handleSurchargeItemChange = (index, field, value) => {
    setSurchargeItems((prev) => {
      const updated = [...prev];
      const newValue =
        field === "quantity" ||
        field === "costCode" ||
        field === "customer" ||
        field === "budget"
          ? field === "quantity"
            ? Math.ceil(parseFloat(value) || 0)
            : field === "customer" || field === "budget"
              ? value
              : value
          : Number(parseFloat(value).toFixed(2)) || 0;
      updated[index] = {
        ...updated[index],
        [field]: newValue,
        amount:
          field === "quantity" || field === "unitCost"
            ? Number(
                (
                  (field === "quantity" ? newValue : updated[index].quantity) *
                  (field === "unitCost" ? newValue : updated[index].unitCost)
                ).toFixed(2),
              )
            : updated[index].amount,
        ...(field === "unitPrice" && { price: newValue, unitPrice: newValue }),
      };
      // Sync with uploadedItems
      setUploadedItems((prevItems) =>
        prevItems.map((item) =>
          item.uploadId === updated[index].uploadId
            ? { ...item, ...updated[index] }
            : item,
        ),
      );
      return updated;
    });
  };

  const handleAddLaborItem = () => {
    if (
      !newLaborItem.labourTitle ||
      newLaborItem.unitCost <= 0 ||
      newLaborItem.unitPrice <= 0 ||
      !newLaborItem.costCode
    ) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message:
          "Labour description, quantity, unit cost, unit price, and cost code are required.",
        onConfirm: () => setAlert({ ...alert, open: false }),
      });
      return;
    }
    const newItem = {
      uploadId: `manual-${Date.now()}-${Math.random()
        .toString(36)
        .substr(2, 9)}`,
      labourTitle: newLaborItem.labourTitle,
      quantity: newLaborItem.quantity,
      unitCost: newLaborItem.unitCost,
      unitPrice: newLaborItem.unitPrice,
      type: "labor",
      category: "L",
      costCodeDescription: newLaborItem.labourTitle,
      amount: Number(
        (newLaborItem.quantity * newLaborItem.unitCost).toFixed(2),
      ),
      uom: newLaborItem.uom,
      laborHours: 0,
      bidArea: newLaborItem.bidArea,
      costCode: newLaborItem.costCode,
      sectionNumber:
        newLaborItem.sectionNumber ||
        costCodes
          .find((cc) => cc._id === newLaborItem.costCode)
          ?.code.split(" - ")[0] ||
        "",
      customer: newLaborItem.customer,
      budget: newLaborItem.budget,
    };
    setLaborItems((prev) => [...prev, newItem]);
    setUploadedItems((prev) => [...prev, newItem]);
    setNewLaborItem({
      labourTitle: "",
      quantity: 0,
      unitCost: 0,
      unitPrice: 0,
      uom: "",
      costCode: "",
      bidArea: "",
      customer: true,
      budget: true,
      sectionNumber: "",
    });
    setSelectedLabourOption(null);
    setAlert({
      open: true,
      type: "success",
      title: "Success",
      message: "Labor item added successfully!",
      onConfirm: () => setAlert({ ...alert, open: false }),
    });
  };

  const handleAddSurchargeItem = () => {
    if (
      !newSurchargeItem.title ||
      newSurchargeItem.unitCost <= 0 ||
      newSurchargeItem.unitPrice <= 0 ||
      !newSurchargeItem.costCode
    ) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message:
          "Title, quantity, unit cost, unit price, and cost code are required for surcharges.",
        onConfirm: () => setAlert({ ...alert, open: false }),
      });
      return;
    }
    const newItem = {
      uploadId: `surcharge-${Date.now()}-${Math.random()
        .toString(36)
        .substr(2, 9)}`,
      title: newSurchargeItem.title,
      quantity: newSurchargeItem.quantity,
      unitCost: newSurchargeItem.unitCost,
      price: newSurchargeItem.unitPrice,
      type: "labor",
      category: "S",
      costCodeDescription: newSurchargeItem.title,
      amount: Number(
        (newSurchargeItem.quantity * newSurchargeItem.unitCost).toFixed(2),
      ),
      uom: "N/A",
      laborHours: 0,
      bidArea: "",
      costCode: newSurchargeItem.costCode,
      sectionNumber:
        costCodes
          .find((cc) => cc._id === newSurchargeItem.costCode)
          ?.code.split(" - ")[0] || "",
      customer: newSurchargeItem.customer,
      budget: newSurchargeItem.budget,
      surcharge: true, // Explicitly mark as surcharge
    };
    setSurchargeItems((prev) => [...prev, newItem]);
    setNewSurchargeItem({
      title: "",
      quantity: 0,
      unitCost: 0,
      unitPrice: 0,
      costCode: "",
      customer: true,
      surcharge: true, // Explicitly mark as surcharge
      budget: true,
    });
    setAlert({
      open: true,
      type: "success",
      title: "Success",
      message: "Surcharge item added successfully!",
      onConfirm: () => setAlert({ ...alert, open: false }),
    });
  };

  const handleDeleteLaborItem = (index) => {
    setLaborItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDeleteSurchargeItem = (index) => {
    setSurchargeItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDeleteMaterialItem = (index) => {
    setSelectedMaterials((prev) => prev.filter((_, i) => i !== index));
    setUploadedItems((prev) =>
      prev.filter(
        (item) => !(item.type === "material" && prev.indexOf(item) === index),
      ),
    );
  };

  const getUserEmailFromToken = () => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decoded = jwtDecode(token);
        return decoded.email;
      } catch (err) {
        console.error("Error decoding token:", err);
        return null;
      }
    }
    return null;
  };

  const handleLabourItemSelect = (selectedOption) => {
    setSelectedLabourOption(selectedOption);
    if (selectedOption) {
      let costCode = selectedOption.costCode || null;
      if (!costCode && selectedOption.sectionNumber) {
        const sectionNumber = selectedOption.sectionNumber.trim().toLowerCase();
        const matchingCostCode = costCodes.find((cc) =>
          cc.code?.trim().toLowerCase().includes(sectionNumber),
        );
        costCode = matchingCostCode ? matchingCostCode._id : null;
      }
      setNewLaborItem((prev) => ({
        ...prev,
        labourTitle: selectedOption.label?.split(" (")[0] || "",
        unitCost: Number(
          parseFloat(
            selectedOption.source === "budget"
              ? selectedOption.unitRate || 0
              : selectedOption.unitCost || 0,
          ).toFixed(2),
        ),
        unitPrice: Number(
          parseFloat(
            selectedOption.source === "budget"
              ? selectedOption.unitRate || 0
              : selectedOption.unitRate || 0,
          ).toFixed(2),
        ),
        uom: selectedOption.uom || "",
        bidArea: selectedOption.bidArea || "",
        costCode,
        sectionNumber: selectedOption.sectionNumber || "",
      }));
    } else {
      setNewLaborItem({
        labourTitle: "",
        quantity: 0,
        unitCost: 0,
        unitPrice: 0,
        uom: "",
        costCode: "",
        bidArea: "",
        customer: true,
        budget: true,
        sectionNumber: "",
      });
    }
  };

  const totalCost = Number(
    (
      selectedMaterials
        .filter((item) => item.budget)
        .reduce((sum, item) => sum + (item.unitCost * item.quantity || 0), 0) +
      laborItems
        .filter((item) => item.budget)
        .reduce((sum, item) => sum + (item.unitCost * item.quantity || 0), 0) +
      surchargeItems
        .filter((item) => item.budget)
        .reduce((sum, item) => sum + (item.unitCost * item.quantity || 0), 0)
    ).toFixed(2),
  );

  const totalPriceForCustomerWithoutOH = Number(
    (
      selectedMaterials
        .filter((item) => item.customer)
        .reduce((sum, item) => sum + (item.price * item.quantity || 0), 0) +
      laborItems
        .filter((item) => item.customer)
        .reduce((sum, item) => sum + (item.unitPrice * item.quantity || 0), 0) +
      surchargeItems
        .filter((item) => item.customer)
        .reduce((sum, item) => sum + (item.unitPrice * item.quantity || 0), 0)
    ).toFixed(2),
  );

  const overheadAmount =
    totalPriceForCustomerWithoutOH * (formData.overheadPercentage / 100);
  const totalPriceForCustomer = Number(
    (totalPriceForCustomerWithoutOH + overheadAmount).toFixed(2),
  );
  const markup =
    totalCost > 0
      ? Number(
          (((totalPriceForCustomer - totalCost) / totalCost) * 100).toFixed(2),
        )
      : 0;

  const handleBillableToggle = () => {
    setSummary((prev) => ({
      ...prev,
      billable: !prev.billable,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const userEmail = getUserEmailFromToken();
    if (!userEmail) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "User email not found. Please log in again.",
        onConfirm: () => setAlert({ ...alert, open: false }),
      });
      return;
    }
    if (!formData.title && !editId) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "Title is required for new change orders.",
        onConfirm: () => setAlert({ ...alert, open: false }),
      });
      return;
    }
    try {
      setIsLoading(true);
      const token = localStorage.getItem("token");
      const allItems = [
        ...selectedMaterials.map((item) => ({
          title: item.title,
          quantity: Number(item.quantity || 0),
          unitCost: Number((item.unitCost || 0).toFixed(2)),
          price: Number((item.price || 0).toFixed(2)),
          unitPrice: Number((item.price || 0).toFixed(2)),
          amount: Number((item.quantity * item.unitCost).toFixed(2)),
          type: "material",
          category: item.category || "M",
          costCodeDescription: item.costCodeDescription || item.title,
          itemCode: item.itemCode || "",
          itemDescription: item.itemDescription || item.title,
          size: item.size || "",
          requiredQty: Number(item.requiredQty || item.quantity || 0),
          container: item.container || "",
          bidArea: item.bidArea || "",
          vendor: item.vendor || null,
          costCode: item.costCode || null,
          sectionNumber: item.sectionNumber || "",
          customer: item.customer !== undefined ? item.customer : true,
          budget: item.budget !== undefined ? item.budget : true,
        })),
        ...laborItems.map((item) => ({
          title: item.labourTitle,
          quantity: Number(item.quantity || 0),
          unitCost: Number((item.unitCost || 0).toFixed(2)),
          unitPrice: Number((item.unitPrice || 0).toFixed(2)),
          price: Number((item.unitPrice || 0).toFixed(2)),
          amount: Number((item.quantity * item.unitCost).toFixed(2)),
          type: "labor",
          category: item.category || "L",
          costCodeDescription: item.costCodeDescription || item.labourTitle,
          uom: item.uom || "",
          laborHours: Number(item.laborHours || 0),
          bidArea: item.bidArea || "",
          costCode: item.costCode || null,
          sectionNumber: item.sectionNumber || "",
          customer: item.customer !== undefined ? item.customer : true,
          budget: item.budget !== undefined ? item.budget : true,
        })),
        ...surchargeItems.map((item) => ({
          title: item.title,
          quantity: Number(item.quantity || 0),
          unitCost: Number((item.unitCost || 0).toFixed(2)),
          unitPrice: Number((item.unitPrice || item.price || 0).toFixed(2)),
          price: Number((item.unitPrice || item.price || 0).toFixed(2)),
          amount: Number((item.quantity * item.unitCost).toFixed(2)),
          type: "labor",
          category: "S",
          costCodeDescription: item.costCodeDescription || item.title,
          uom: "N/A",
          laborHours: 0,
          bidArea: "",
          costCode: item.costCode || null,
          sectionNumber: item.sectionNumber || "",
          customer: item.customer !== undefined ? item.customer : true,
          budget: item.budget !== undefined ? item.budget : true,
          surcharge: true,
        })),
      ];
      const changeOrderData = {
        title: formData.title,
        termsAndConditions: formData.termsAndConditions,
        approvalStatus: formData.approvalStatus,
        overheadPercentage: Number(formData.overheadPercentage || 0),
        changesToSchedule: Number(formData.changesToSchedule || 0),
        totalPrice: Number(totalPriceForCustomer.toFixed(2)),
        totalCost: Number(totalCost.toFixed(2)),
        items: allItems,
        summary: {
          description: summary.description,
          qtyUnit: summary.qtyUnit,
          billable: summary.billable,
          revision: summary.revision || 0,
        },
        project: id,
        userEmail,
      };
      let response;
      if (editId) {
        if (formData.approvalStatus === "Approved") {
          response = await axios.put(
            `https://erp-production-72da01c8e651.herokuapp.com/api/changeOrders/${editId}/approve`,
            {
              summary: {
                billable: summary.billable,
                revision: summary.revision || 0,
              },
            },
            { headers: { Authorization: `Bearer ${token}` } },
          );
        } else {
          response = await axios.put(
            `https://erp-production-72da01c8e651.herokuapp.com/api/changeOrders/${editId}`,
            changeOrderData,
            { headers: { Authorization: `Bearer ${token}` } },
          );
        }
      } else {
        response = await axios.post(
          "https://erp-production-72da01c8e651.herokuapp.com/api/changeOrders",
          changeOrderData,
          { headers: { Authorization: `Bearer ${token}` } },
        );
      }
      setSavedChangeOrder(response.data.data || response.data);
      setProject((prev) => ({
        ...prev,
        changeOrders: editId
          ? (prev.changeOrders || []).map((co) =>
              co._id === editId ? response.data.data || response.data : co,
            )
          : [...(prev.changeOrders || []), response.data.data || response.data],
      }));
      console.log(response.data);

      setAlert({
        open: true,
        type: "success",
        title: "Success",
        message:
          response.data.message ||
          (editId
            ? "Change Order updated successfully!"
            : `Change Order created with number: ${
                response.data.changeOrderNumber || "unknown"
              }`),
        onConfirm: () => {
          setAlert({ ...alert, open: false });
          navigate(`/Projects/${id}/change-orders/${response.data._id}/edit`);
        },
      });

      navigate(`/Projects/${id}/change-orders/${response.data._id}/edit`);
    } catch (err) {
      console.error("Error saving change order:", err);
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message:
          "Failed to save change order: " +
          (err.response?.data?.message || err.message),
        onConfirm: () => setAlert({ ...alert, open: false }),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSummaryChange = (e) => {
    const { name, value } = e.target;
    setSummary((prev) => ({ ...prev, [name]: value }));
  };

  const formatNumber = (num) =>
    Number.isFinite(num)
      ? `$${num.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`
      : "$0.00";

  const generateChangeOrderPDF = async () => {
    if (!companyProfile) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "Cannot generate PDF: Company profile is not set.",
        onConfirm: () => setAlert({ ...alert, open: false }),
      });
      return;
    }
    try {
      const response = await fetch("/change_order_template.docx");
      if (!response.ok)
        throw new Error(`Failed to load template: ${response.statusText}`);
      const templateArrayBuffer = await response.arrayBuffer();
      const zip = new PizZip(templateArrayBuffer);
      const base64Regex =
        /^(?:data:)?image\/(png|jpg|jpeg|svg|svg\+xml);base64,/;
      const validBase64 =
        /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;
      function base64Parser(tagValue) {
        if (typeof tagValue !== "string" || !base64Regex.test(tagValue))
          return null;
        const stringBase64 = tagValue.replace(base64Regex, "");
        if (!validBase64.test(stringBase64))
          throw new Error("Error parsing base64 data, invalid characters");
        const binaryString = window.atob(stringBase64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) bytes[i] = binaryString.charCodeAt(i);
        return bytes.buffer;
      }
      const formatPrice = (value) => {
        return `$${Number(value).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`;
      };
      let imageBase64;
      if (
        companyProfile?.logo &&
        !companyProfile.logo.startsWith("data:image")
      ) {
        const logoResponse = await fetch(companyProfile.logo);
        if (logoResponse.ok) {
          const logoBlob = await logoResponse.blob();
          imageBase64 = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(logoBlob);
          });
        }
      } else if (companyProfile?.logo) {
        imageBase64 = companyProfile.logo;
      }
      const imageOptions = {
        getImage: (tagValue) => base64Parser(tagValue),
        getSize: () => [125, 90],
      };
      const doc = new Docxtemplater(zip, {
        paragraphLoop: true,
        linebreaks: true,
        modules: [new ImageModule(imageOptions)],
      });
      const materialSubtotal = selectedMaterials
        .filter((item) => item.customer)
        .reduce((sum, item) => sum + item.quantity * item.price, 0);
      const labourTotal = laborItems
        .filter((item) => item.customer)
        .reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);
      const surchargeTotal = surchargeItems
        .filter((item) => item.customer)
        .reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);
      const subtotal = materialSubtotal + labourTotal + surchargeTotal;
      const overhead = subtotal * (Number(formData.overheadPercentage) / 100);
      const total = totalPriceForCustomer;
      const projectAccount =
        project?.accountId?.companyName || "Unknown Contractor";
      const customerContact = project?.customerContact
        ? `${project.customerContact.firstName} ${project.customerContact.lastName}`
        : "N/A";
      const data = {
        changeOrderNumber: savedChangeOrder?.changeOrderNumber || "TBD",
        title: formData.title,
        projectName: project?.projectName || "Unknown Project",
        date: new Date().toLocaleDateString(),
        projectAccount,
        customerContact,
        description: summary.description || "N/A",
        materials: selectedMaterials
          .filter((item) => item.customer)
          .map((item) => ({
            title: item.title,
            unitPrice: formatPrice(item.price),
            materialQuantity: item.quantity,
            itemTotal: formatPrice(item.quantity * item.price),
          })),
        materialSubtotal: formatPrice(materialSubtotal),
        labourItems: laborItems
          .filter((item) => item.customer)
          .map((item) => ({
            labourTitle: item.labourTitle,
            labourQuantity: item.quantity,
            labourRate: formatPrice(item.unitPrice),
            labourItemTotal: formatPrice(item.quantity * item.unitPrice),
          })),
        labourTotal: formatPrice(labourTotal),
        surchargeItems: surchargeItems
          .filter((item) => item.customer)
          .map((item) => ({
            title: item.title,
            quantity: item.quantity,
            unitPrice: formatPrice(item.unitPrice),
            itemTotal: formatPrice(item.quantity * item.unitPrice),
          })),
        surchargeTotal: formatPrice(surchargeTotal),
        subtotal: formatPrice(totalPriceForCustomerWithoutOH),
        ohPrecent: formData.overheadPercentage.toFixed(2),
        overhead: formatPrice(overhead),
        total: formatPrice(totalPriceForCustomer),
        changestoSchedule: formData.changesToSchedule,
        termsAndConditions: formData.termsAndConditions || "N/A",
        image: imageBase64,
      };
      doc.setData(data);
      doc.render();
      const docxBlob = doc.getZip().generate({
        type: "blob",
        mimeType:
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });
      const formDataPDF = new FormData();
      formDataPDF.append(
        "docx",
        docxBlob,
        `ChangeOrder_${data.changeOrderNumber}.docx`,
      );
      formDataPDF.append("documentType", "ChangeOrder");
      formDataPDF.append("identifier", data.changeOrderNumber);
      const token = localStorage.getItem("token");
      const pdfResponse = await axios.post(
        "https://erp-production-72da01c8e651.herokuapp.com/api/upload/convert-to-pdf",
        formDataPDF,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
          responseType: "blob",
        },
      );
      if (
        pdfResponse.status === 200 &&
        pdfResponse.headers["content-type"] === "application/pdf"
      ) {
        const pdfBlob = new Blob([pdfResponse.data], {
          type: "application/pdf",
        });
        saveAs(pdfBlob, `ChangeOrder_${data.changeOrderNumber}.pdf`);
        setAlert({
          open: true,
          type: "success",
          title: "Success",
          message: "Change Order PDF generated successfully!",
          onConfirm: () => setAlert({ ...alert, open: false }),
        });
      } else {
        throw new Error("Failed to convert DOCX to PDF");
      }
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "Failed to generate PDF: " + err.message,
        onConfirm: () => setAlert({ ...alert, open: false }),
      });
      console.error("Error:", err);
    }
  };

  if (isLoading)
    return (
      <div className="text-center py-4 text-gray-200 bg-gray-700">
        Loading...
      </div>
    );
  if (!project)
    return (
      <div className="text-red-400 text-center py-4 bg-gray-700">
        Project not found.
      </div>
    );

  return (
    <div className="max-w-full mx-auto px-4 py-6">
      <AlertDialog
        open={alert.open}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        onClose={() => setAlert({ ...alert, open: false })}
        onConfirm={() => setAlert({ ...alert, open: false })}
      />
      <div className="mb-6 shadow-lg bg-gray-700 rounded-lg p-6 border border-gray-600">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-semibold text-white">
            {editId ? "Edit Change Order" : "Generate Change Order"}
          </h1>
          <button
            onClick={() =>
              navigate(`/Projects/${project._id}`, {
                state: { initialTab: "Change Order (CO)" },
              })
            }
            className="px-4 py-2 text-sm font-medium text-gray-200 bg-gray-600 rounded-md hover:bg-gray-500 transition-colors duration-200"
          >
            Back
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-gray-700 p-4 rounded-lg border border-gray-600">
          <h2 className="text-lg font-semibold text-white mb-3">
            Change Order Details
          </h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-6 md:gap-x-4 md:gap-y-3">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-400">
                Title
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Title"
                className="mt-1 block w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                required
              />
            </div>

            <div className="flex space-x-1 md:col-span-2">
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Overhead (%)
                </label>
                <input
                  type="number"
                  name="overheadPercentage"
                  value={formData.overheadPercentage}
                  onChange={handleChange}
                  placeholder="0"
                  step="0.01"
                  className="mt-1 block w-20 rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                />
              </div>
              <div className="px-4">
                <label className="block text-sm font-medium text-gray-400">
                  Changed (Days)
                </label>
                <input
                  type="number"
                  name="changesToSchedule"
                  value={formData.changesToSchedule}
                  onChange={handleChange}
                  placeholder="0"
                  step="1"
                  className="mt-1 block w-20 rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                />
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-400">
                Approval Status
              </label>
              <select
                name="approvalStatus"
                value={formData.approvalStatus}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
              >
                {formData.approvalStatus === "Approved" ? (
                  <>
                    <option value="Approved">Approved</option>
                    <option value="Rejected">Rejected</option>
                  </>
                ) : (
                  <>
                    <option value="Pending">Pending</option>
                    <option value="Approved">Approved</option>
                    <option value="Rejected">Rejected</option>
                  </>
                )}
              </select>
            </div>
            <div className="md:col-span-4">
              <label className="block text-sm font-medium text-gray-400">
                Terms and Conditions
              </label>
              <textarea
                name="termsAndConditions"
                value={formData.termsAndConditions}
                onChange={handleChange}
                placeholder="Terms and Conditions"
                className="mt-1 block w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                rows="2"
              />
            </div>
          </div>
        </div>

        <div className="bg-gray-700 p-6 rounded-lg border border-gray-600">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-white">Materials</h2>
            <div className="space-x-4">
              <button
                type="button"
                onClick={() => setShowMaterialInput(!showMaterialInput)}
                className="px-4 py-2 text-sm font-medium text-gray-200 bg-orange-400 rounded-md hover:bg-orange-500 transition-colors duration-200"
              >
                {showMaterialInput ? "Hide Add Material" : "Add Line Item"}
              </button>
              <button
                type="button"
                onClick={() => setShowBudgetUpload(!showBudgetUpload)}
                className="px-4 py-2 text-sm font-medium text-gray-200 bg-orange-400 rounded-md hover:bg-orange-500 transition-colors duration-200"
              >
                {showBudgetUpload
                  ? "Hide Upload"
                  : "Upload Change Order Budget"}
              </button>
            </div>
          </div>
          {showBudgetUpload && (
            <div className="space-y-4 mb-4">
              <input
                type="file"
                accept=".csv, .xlsx"
                onChange={(e) => setBudgetFile(e.target.files[0])}
                className="block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-gray-600 file:text-gray-200 hover:file:bg-gray-500"
              />
              <button
                onClick={(e) => handleBudgetFileUpload(e)}
                disabled={!budgetFile}
                className={`px-4 py-2 text-sm font-medium text-gray-200 rounded-md ${
                  budgetFile
                    ? "bg-orange-400 hover:bg-orange-500 transition-colors duration-200"
                    : "bg-gray-500 cursor-not-allowed"
                }`}
              >
                Upload Budget
              </button>
            </div>
          )}
          {showMaterialInput && (
            <div className="space-y-4 mb-4">
              <div className="flex flex-wrap gap-4">
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-sm font-medium text-gray-400 mt-1">
                    Select Material Item
                  </label>
                  <Select
                    options={availableMaterialItems}
                    value={selectedMaterialOption}
                    onChange={handleMaterialItemSelect}
                    placeholder="Select a material item"
                    styles={customStyles}
                    isClearable
                    menuPortalTarget={document.body}
                  />
                </div>
                <div className="flex-1 min-w-[240px]">
                  <label className="block text-sm font-medium text-gray-300">
                    Title{" "}
                    <span className="text-red-400 text-xs">(required)</span>
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={newMaterialItem.title}
                    onChange={handleNewMaterialChange}
                    placeholder="e.g. 3/4\ Plywood"
                    className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-white focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400">
                    Cost Code
                  </label>
                  <Select
                    options={costCodeOptions}
                    value={
                      costCodeOptions.find(
                        (option) => option.value === newMaterialItem.costCode,
                      ) || null
                    }
                    onChange={(option) =>
                      setNewMaterialItem((prev) => ({
                        ...prev,
                        costCode: option ? option.value : "",
                      }))
                    }
                    placeholder="Select Cost Code"
                    styles={customStyles}
                    className="w-48 mt-1"
                    menuPortalTarget={document.body}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-400">
                    Bid Area
                  </label>
                  <CreatableSelect
                    options={bidAreaOptions}
                    value={
                      bidAreaOptions.find(
                        (option) => option.value === newMaterialItem.bidArea,
                      ) ||
                      (newMaterialItem.bidArea
                        ? {
                            value: newMaterialItem.bidArea,
                            label: newMaterialItem.bidArea,
                          }
                        : null)
                    }
                    onChange={(option) =>
                      setNewMaterialItem((prev) => ({
                        ...prev,
                        bidArea: option ? option.value : "",
                      }))
                    }
                    onCreateOption={(inputValue) => {
                      const trimmedValue = inputValue.trim();
                      if (!trimmedValue) {
                        setAlert({
                          open: true,
                          type: "error",
                          title: "Invalid Bid Area",
                          message: "Bid area cannot be empty.",
                          onConfirm: () => setAlert({ ...alert, open: false }),
                        });
                        return;
                      }
                      const newOption = {
                        value: trimmedValue,
                        label: trimmedValue,
                      };
                      setBidAreaOptions((prev) => [...prev, newOption]);
                      setNewMaterialItem((prev) => ({
                        ...prev,
                        bidArea: trimmedValue,
                      }));
                      setAlert({
                        open: true,
                        type: "success",
                        title: "Bid Area Created",
                        message: `New bid area "${trimmedValue}" created successfully!`,
                        onConfirm: () => setAlert({ ...alert, open: false }),
                      });
                    }}
                    placeholder="Select or type Bid Area"
                    styles={customStyles}
                    className="w-48 mt-1"
                    menuPortalTarget={document.body}
                    formatCreateLabel={(inputValue) => `Create "${inputValue}"`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400">
                    Vendor
                  </label>
                  <Select
                    options={vendorOptions}
                    value={
                      vendorOptions.find(
                        (option) => option.value === newMaterialItem.vendor,
                      ) || null
                    }
                    onChange={(option) =>
                      setNewMaterialItem((prev) => ({
                        ...prev,
                        vendor: option ? option.value : null,
                      }))
                    }
                    placeholder="Select Vendor"
                    styles={customStyles}
                    className="w-48 mt-1"
                    menuPortalTarget={document.body}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400">
                    Quantity
                  </label>
                  <input
                    type="number"
                    name="quantity"
                    value={newMaterialItem.quantity}
                    onChange={handleNewMaterialChange}
                    placeholder="Quantity"
                    step="1"
                    className="mt-1 w-20 rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400">
                    Unit Cost
                  </label>
                  <input
                    type="number"
                    name="unitCost"
                    value={newMaterialItem.unitCost}
                    onChange={handleNewMaterialChange}
                    placeholder="Unit Cost"
                    step="0.01"
                    className="mt-1 w-20 rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400">
                    Unit Price
                  </label>
                  <input
                    type="number"
                    name="price"
                    value={newMaterialItem.price}
                    onChange={handleNewMaterialChange}
                    placeholder="Unit Price"
                    step="0.01"
                    className="mt-1 w-20 rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400">
                    Description
                  </label>
                  <div className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-white sm:text-sm">
                    {newMaterialItem.itemDescription}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400">
                    Item Code
                  </label>
                  <input
                    type="text"
                    name="itemCode"
                    value={newMaterialItem.itemCode}
                    onChange={handleNewMaterialChange}
                    placeholder="Item Code"
                    className="mt-1 w-56 rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400">
                    Size
                  </label>
                  <input
                    type="text"
                    name="size"
                    value={newMaterialItem.size}
                    onChange={handleNewMaterialChange}
                    placeholder="Size"
                    className="mt-1 w-24 rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400">
                    Container
                  </label>
                  <input
                    type="text"
                    name="container"
                    value={newMaterialItem.container}
                    onChange={handleNewMaterialChange}
                    placeholder="Container"
                    className="mt-1 w-24 rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400">
                    Customer
                  </label>
                  <input
                    type="checkbox"
                    name="customer"
                    checked={newMaterialItem.customer}
                    onChange={(e) =>
                      setNewMaterialItem((prev) => ({
                        ...prev,
                        customer: e.target.checked,
                      }))
                    }
                    className="mt-1 h-4 w-4 text-orange-400 focus:ring-orange-500 border-gray-500 rounded"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400">
                    Budget
                  </label>
                  <input
                    type="checkbox"
                    name="budget"
                    checked={newMaterialItem.budget}
                    onChange={(e) =>
                      setNewMaterialItem((prev) => ({
                        ...prev,
                        budget: e.target.checked,
                      }))
                    }
                    className="mt-1 h-4 w-4 text-orange-400 focus:ring-orange-500 border-gray-500 rounded"
                  />
                </div>
                <div className="flex items-end">
                  <button
                    type="button"
                    onClick={handleAddMaterialItem}
                    className="px-4 py-2 text-sm font-medium text-gray-200 bg-orange-400 rounded-md hover:bg-orange-500 transition-colors duration-200"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-600 bg-gray-700">
              <thead className="bg-gray-800">
                <tr>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Select
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Budget
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider max-w-lg break-words whitespace-normal">
                    Title
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Cost Code
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Bid Area
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Vendor
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Size
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Quantity
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Unit Cost
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Unit Price
                  </th>
                  <th className="px-62py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Item Total Cost
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Item Total Price
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-600">
                {uploadedItems.filter((item) => item.type === "material")
                  .length === 0 ? (
                  <tr>
                    <td
                      colSpan="13"
                      className="px-6 py-4 text-center text-sm text-gray-400"
                    >
                      No materials available. Please upload a change order
                      budget file or add items manually.
                    </td>
                  </tr>
                ) : (
                  uploadedItems
                    .filter((item) => item.type === "material")
                    .map((item, index) => {
                      const selected = selectedMaterials.find(
                        (m) => m.uploadId === item.uploadId,
                      );
                      return (
                        <tr
                          key={item.uploadId}
                          className={
                            selected
                              ? "bg-orange-600/20"
                              : "hover:bg-gray-500 transition-colors duration-200"
                          }
                        >
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-200">
                            <input
                              type="checkbox"
                              checked={!!selected}
                              onChange={() =>
                                handleMaterialSelect(item.uploadId)
                              }
                              className="h-4 w-4 text-orange-400 focus:ring-orange-500 border-gray-500 rounded"
                            />
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-200">
                            <input
                              type="checkbox"
                              checked={
                                selected ? selected.customer : item.customer
                              }
                              onChange={(e) =>
                                selected &&
                                handleMaterialChange(
                                  selectedMaterials.findIndex(
                                    (m) => m.uploadId === item.uploadId,
                                  ),
                                  "customer",
                                  e.target.checked,
                                )
                              }
                              disabled={!selected}
                              className="h-4 w-4 text-orange-400 focus:ring-orange-500 border-gray-500 rounded"
                            />
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-200">
                            <input
                              type="checkbox"
                              checked={selected ? selected.budget : item.budget}
                              onChange={(e) =>
                                selected &&
                                handleMaterialChange(
                                  selectedMaterials.findIndex(
                                    (m) => m.uploadId === item.uploadId,
                                  ),
                                  "budget",
                                  e.target.checked,
                                )
                              }
                              disabled={!selected}
                              className="h-4 w-4 text-orange-400 focus:ring-orange-500 border-gray-500 rounded"
                            />
                          </td>
                          <td className="px-2 py-4 text-sm text-gray-200 max-w-md break-words whitespace-normal">
                            {item.title}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-200">
                            <Select
                              options={costCodeOptions}
                              value={
                                costCodeOptions.find(
                                  (option) =>
                                    option.value ===
                                    (selected
                                      ? selected.costCode
                                      : item.costCode),
                                ) || null
                              }
                              onChange={(option) =>
                                selected &&
                                handleMaterialChange(
                                  selectedMaterials.findIndex(
                                    (m) => m.uploadId === item.uploadId,
                                  ),
                                  "costCode",
                                  option ? option.value : null,
                                )
                              }
                              placeholder="Select Cost Code"
                              isDisabled={!selected}
                              styles={customStyles}
                              className="w-28"
                              menuPortalTarget={document.body}
                            />
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-200">
                            <Select
                              options={bidAreaOptions}
                              value={
                                bidAreaOptions.find(
                                  (option) =>
                                    option.value ===
                                    (selected
                                      ? selected.bidArea
                                      : item.bidArea),
                                ) || null
                              }
                              onChange={(option) =>
                                selected &&
                                handleMaterialChange(
                                  selectedMaterials.findIndex(
                                    (m) => m.uploadId === item.uploadId,
                                  ),
                                  "bidArea",
                                  option ? option.value : "",
                                )
                              }
                              placeholder="Select Bid Area"
                              isDisabled={!selected}
                              styles={customStyles}
                              className="w-32"
                              menuPortalTarget={document.body}
                            />
                          </td>
                          <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-200">
                            <Select
                              options={vendorOptions}
                              value={
                                vendorOptions.find(
                                  (option) =>
                                    option.value ===
                                    (selected ? selected.vendor : item.vendor),
                                ) || null
                              }
                              onChange={(option) =>
                                selected &&
                                handleMaterialChange(
                                  selectedMaterials.findIndex(
                                    (m) => m.uploadId === item.uploadId,
                                  ),
                                  "vendor",
                                  option ? option.value : null,
                                )
                              }
                              placeholder="Select Vendor"
                              isDisabled={!selected}
                              styles={customStyles}
                              className="w-32"
                              menuPortalTarget={document.body}
                            />
                          </td>
                          <td className="px-2 py-4 text-sm text-gray-200 max-w-md break-words whitespace-normal">
                            {selected ? selected.size : item.size}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-200">
                            <input
                              type="number"
                              step="1"
                              value={
                                selected ? selected.quantity : item.quantity
                              }
                              onChange={(e) =>
                                selected &&
                                handleMaterialChange(
                                  selectedMaterials.findIndex(
                                    (m) => m.uploadId === item.uploadId,
                                  ),
                                  "quantity",
                                  e.target.value,
                                )
                              }
                              disabled={!selected}
                              className="w-20 rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                            />
                          </td>
                          <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-200">
                            <input
                              type="number"
                              step="0.01"
                              value={
                                selected ? selected.unitCost : item.unitCost
                              }
                              onChange={(e) =>
                                selected &&
                                handleMaterialChange(
                                  selectedMaterials.findIndex(
                                    (m) => m.uploadId === item.uploadId,
                                  ),
                                  "unitCost",
                                  e.target.value,
                                )
                              }
                              disabled={!selected}
                              className="w-24 rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                            />
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-200">
                            <input
                              type="number"
                              step="0.01"
                              value={selected ? selected.price : item.price}
                              onChange={(e) =>
                                selected &&
                                handleMaterialChange(
                                  selectedMaterials.findIndex(
                                    (m) => m.uploadId === item.uploadId,
                                  ),
                                  "price",
                                  e.target.value,
                                )
                              }
                              disabled={!selected}
                              className="w-24 rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                            />
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-200">
                            {formatNumber(
                              (selected ? selected.quantity : item.quantity) *
                                (selected ? selected.unitCost : item.unitCost),
                            )}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-200">
                            {formatNumber(
                              (selected ? selected.quantity : item.quantity) *
                                (selected ? selected.price : item.price),
                            )}
                          </td>
                        </tr>
                      );
                    })
                )}
              </tbody>
              <tfoot>
                <tr className="bg-gray-800">
                  <td
                    colSpan="11"
                    className="px-6 py-3 text-right text-sm font-medium text-gray-200"
                  >
                    Total:
                  </td>
                  <td className="px-6 py-3 text-left text-sm font-medium text-gray-200">
                    {formatNumber(
                      selectedMaterials
                        .filter((item) => item.budget)
                        .reduce(
                          (sum, item) =>
                            sum + (item.quantity * item.unitCost || 0),
                          0,
                        ),
                    )}
                  </td>
                  <td className="px-6 py-3 text-left text-sm font-medium text-gray-200">
                    {formatNumber(
                      selectedMaterials
                        .filter((item) => item.customer)
                        .reduce(
                          (sum, item) =>
                            sum + (item.quantity * item.price || 0),
                          0,
                        ),
                    )}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <div className="bg-gray-700 p-6 rounded-lg border border-gray-600">
          <h2 className="text-lg font-semibold text-white mb-4">Labor</h2>
          <div className="space-y-4 overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-600 bg-gray-700">
              <thead className="bg-gray-800">
                <tr>
                  <th className="px-1 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-2 py-3 text-left text-xs/3 font-medium text-gray-400 uppercase tracking-wider">
                    Budget
                  </th>
                  <th className="px-6 min-w-[260px] py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Cost Code
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Quantity
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Unit
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Bid Area
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Unit Cost
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Unit Price
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Item Total Cost
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Item Total Price
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-600">
                {laborItems.length === 0 ? (
                  <tr>
                    <td
                      colSpan="12"
                      className="px-6 py-4 text-center text-sm text-gray-400"
                    >
                      No labor items added. Add items manually below or upload a
                      budget file.
                    </td>
                  </tr>
                ) : (
                  laborItems.map((item, index) => (
                    <tr
                      key={item.uploadId || index}
                      className="hover:bg-gray-500 transition-colors duration-200"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                        <input
                          type="checkbox"
                          checked={item.customer}
                          onChange={(e) =>
                            handleLaborItemChange(
                              index,
                              "customer",
                              e.target.checked,
                            )
                          }
                          className="h-4 w-4 text-orange-400 focus:ring-orange-500 border-gray-500 rounded"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                        <input
                          type="checkbox"
                          checked={item.budget}
                          onChange={(e) =>
                            handleLaborItemChange(
                              index,
                              "budget",
                              e.target.checked,
                            )
                          }
                          className="h-4 w-4 text-orange-400 focus:ring-orange-500 border-gray-500 rounded"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                        <input
                          type="text"
                          value={item.labourTitle}
                          onChange={(e) =>
                            handleLaborItemChange(
                              index,
                              "labourTitle",
                              e.target.value,
                            )
                          }
                          className="w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                        <Select
                          options={costCodeOptions}
                          value={
                            costCodeOptions.find(
                              (option) => option.value === item.costCode,
                            ) || null
                          }
                          onChange={(option) =>
                            handleLaborItemChange(
                              index,
                              "costCode",
                              option ? option.value : null,
                            )
                          }
                          placeholder="Select Cost Code"
                          styles={customStyles}
                          className="w-48"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                        <input
                          type="number"
                          step="1"
                          value={item.quantity}
                          onChange={(e) =>
                            handleLaborItemChange(
                              index,
                              "quantity",
                              e.target.value,
                            )
                          }
                          className="w-20 rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                        <input
                          type="text"
                          value={item.uom}
                          onChange={(e) =>
                            handleLaborItemChange(index, "uom", e.target.value)
                          }
                          className="w-20 rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                        <Select
                          options={bidAreaOptions}
                          value={
                            bidAreaOptions.find(
                              (option) => option.value === item.bidArea,
                            ) || null
                          }
                          onChange={(option) =>
                            handleLaborItemChange(
                              index,
                              "bidArea",
                              option ? option.value : "",
                            )
                          }
                          placeholder="Select Bid Area"
                          styles={customStyles}
                          className="w-32"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                        <input
                          type="number"
                          step="0.01"
                          value={item.unitCost}
                          onChange={(e) =>
                            handleLaborItemChange(
                              index,
                              "unitCost",
                              e.target.value,
                            )
                          }
                          className="w-20 rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                        <input
                          type="number"
                          step="0.01"
                          value={item.unitPrice}
                          onChange={(e) =>
                            handleLaborItemChange(
                              index,
                              "unitPrice",
                              e.target.value,
                            )
                          }
                          className="w-20 rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                        {formatNumber(item.quantity * item.unitCost)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                        {formatNumber(item.quantity * item.unitPrice)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                        <button
                          type="button"
                          onClick={() => handleDeleteLaborItem(index)}
                          className="text-red-400 hover:text-red-500"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot>
                <tr className="bg-gray-800">
                  <td
                    colSpan="9"
                    className="px-6 py-3 text-right text-sm font-medium text-gray-200"
                  >
                    Total:
                  </td>
                  <td className="px-6 py-3 text-left text-sm font-medium text-gray-200">
                    {formatNumber(
                      laborItems
                        .filter((item) => item.budget)
                        .reduce(
                          (sum, item) =>
                            sum + (item.quantity * item.unitCost || 0),
                          0,
                        ),
                    )}
                  </td>
                  <td className="px-6 py-3 text-left text-sm font-medium text-gray-200">
                    {formatNumber(
                      laborItems
                        .filter((item) => item.customer)
                        .reduce(
                          (sum, item) =>
                            sum + (item.quantity * item.unitPrice || 0),
                          0,
                        ),
                    )}
                  </td>
                  <td className="px-6 py-3"></td>
                </tr>
              </tfoot>
            </table>
            <div className="flex space-x-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-400 mt-1">
                  Select Labor Item
                </label>
                <Select
                  options={availableLabourItems}
                  value={selectedLabourOption}
                  onChange={handleLabourItemSelect}
                  placeholder="Select a labor item"
                  styles={customStyles}
                  isClearable
                  menuPortalTarget={document.body}
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-400">
                  Description
                </label>
                <input
                  type="text"
                  name="labourTitle"
                  value={newLaborItem.labourTitle}
                  onChange={handleLaborChange}
                  placeholder="Labour Description"
                  className="mt-1 block w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Cost Code
                </label>
                <Select
                  options={costCodeOptions}
                  value={
                    costCodeOptions.find(
                      (option) => option.value === newLaborItem.costCode,
                    ) || null
                  }
                  onChange={(option) =>
                    setNewLaborItem((prev) => ({
                      ...prev,
                      costCode: option ? option.value : "",
                    }))
                  }
                  placeholder="Select Cost Code"
                  styles={customStyles}
                  className="w-48 mt-1"
                  menuPortalTarget={document.body}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Quantity
                </label>
                <input
                  type="number"
                  name="quantity"
                  value={newLaborItem.quantity}
                  onChange={handleLaborChange}
                  placeholder="Quantity"
                  step="1"
                  className="mt-1 w-20 rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Unit
                </label>
                <input
                  type="text"
                  name="uom"
                  value={newLaborItem.uom}
                  onChange={handleLaborChange}
                  placeholder="Unit"
                  className="mt-1 w-20 rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Bid Area
                </label>
                <Select
                  options={bidAreaOptions}
                  value={
                    bidAreaOptions.find(
                      (option) => option.value === newLaborItem.bidArea,
                    ) || null
                  }
                  onChange={(option) =>
                    setNewLaborItem((prev) => ({
                      ...prev,
                      bidArea: option ? option.value : "",
                    }))
                  }
                  placeholder="Select Bid Area"
                  styles={customStyles}
                  className="w-32 mt-1"
                  menuPortalTarget={document.body}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Unit Cost
                </label>
                <input
                  type="number"
                  name="unitCost"
                  value={newLaborItem.unitCost}
                  onChange={handleLaborChange}
                  placeholder="Unit Cost"
                  step="0.01"
                  className="mt-1 w-20 rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Unit Price
                </label>
                <input
                  type="number"
                  name="unitPrice"
                  value={newLaborItem.unitPrice}
                  onChange={handleLaborChange}
                  placeholder="Unit Price"
                  step="0.01"
                  className="mt-1 w-20 rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Customer
                </label>
                <input
                  type="checkbox"
                  name="customer"
                  checked={newLaborItem.customer}
                  onChange={(e) =>
                    setNewLaborItem((prev) => ({
                      ...prev,
                      customer: e.target.checked,
                    }))
                  }
                  className="mt-1 h-4 w-4 text-orange-400 focus:ring-orange-500 border-gray-500 rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Budget
                </label>
                <input
                  type="checkbox"
                  name="budget"
                  checked={newLaborItem.budget}
                  onChange={(e) =>
                    setNewLaborItem((prev) => ({
                      ...prev,
                      budget: e.target.checked,
                    }))
                  }
                  className="mt-1 h-4 w-4 text-orange-400 focus:ring-orange-500 border-gray-500 rounded"
                />
              </div>
              <div className="flex items-end">
                <button
                  type="button"
                  onClick={handleAddLaborItem}
                  className="px-4 py-2 text-sm font-medium text-gray-200 bg-orange-400 rounded-md hover:bg-orange-500 transition-colors duration-200"
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gray-700 p-6 rounded-lg border border-gray-600">
          <h2 className="text-lg font-semibold text-white mb-4">Surcharges</h2>
          <div className="space-y-4">
            <table className="min-w-full divide-y divide-gray-600 bg-gray-700">
              <thead className="bg-gray-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Budget
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Title
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Cost Code
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Quantity
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Unit Cost
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Unit Price
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Total Cost
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-600">
                {surchargeItems.length === 0 ? (
                  <tr>
                    <td
                      colSpan="9"
                      className="px-6 py-4 text-center text-sm text-gray-400"
                    >
                      No surcharge items added. Add items manually below or
                      upload a budget file.
                    </td>
                  </tr>
                ) : (
                  surchargeItems.map((item, index) => (
                    <tr
                      key={item.uploadId || index}
                      className="hover:bg-gray-500 transition-colors duration-200"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                        <input
                          type="checkbox"
                          checked={item.customer}
                          onChange={(e) =>
                            handleSurchargeItemChange(
                              index,
                              "customer",
                              e.target.checked,
                            )
                          }
                          className="h-4 w-4 text-orange-400 focus:ring-orange-500 border-gray-500 rounded"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                        <input
                          type="checkbox"
                          checked={item.budget}
                          onChange={(e) =>
                            handleSurchargeItemChange(
                              index,
                              "budget",
                              e.target.checked,
                            )
                          }
                          className="h-4 w-4 text-orange-400 focus:ring-orange-500 border-gray-500 rounded"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                        <input
                          type="text"
                          value={item.title}
                          onChange={(e) =>
                            handleSurchargeItemChange(
                              index,
                              "title",
                              e.target.value,
                            )
                          }
                          className="w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                        <Select
                          options={costCodeOptions}
                          value={
                            costCodeOptions.find(
                              (option) => option.value === item.costCode,
                            ) || null
                          }
                          onChange={(option) =>
                            handleSurchargeItemChange(
                              index,
                              "costCode",
                              option ? option.value : null,
                            )
                          }
                          placeholder="Select Cost Code"
                          styles={customStyles}
                          className="w-48"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                        <input
                          type="number"
                          step="1"
                          value={item.quantity}
                          onChange={(e) =>
                            handleSurchargeItemChange(
                              index,
                              "quantity",
                              e.target.value,
                            )
                          }
                          className="w-20 rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                        <input
                          type="number"
                          step="0.01"
                          value={item.unitCost}
                          onChange={(e) =>
                            handleSurchargeItemChange(
                              index,
                              "unitCost",
                              e.target.value,
                            )
                          }
                          className="w-20 rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                        <input
                          type="number"
                          step="0.01"
                          value={item.price}
                          onChange={(e) =>
                            handleSurchargeItemChange(
                              index,
                              "unitPrice",
                              e.target.value,
                            )
                          }
                          className="w-20 rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                        {formatNumber(item.amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                        <button
                          type="button"
                          onClick={() => handleDeleteSurchargeItem(index)}
                          className="text-red-400 hover:text-red-500"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot>
                <tr className="bg-gray-800">
                  <td
                    colSpan="7"
                    className="px-6 py-3 text-right text-sm font-medium text-gray-200"
                  >
                    Total:
                  </td>
                  <td className="px-6 py-3 text-left text-sm font-medium text-gray-200">
                    {formatNumber(
                      surchargeItems
                        .filter((item) => item.budget)
                        .reduce(
                          (sum, item) =>
                            sum + (item.quantity * item.unitCost || 0),
                          0,
                        ),
                    )}
                  </td>
                  <td className="px-6 py-3"></td>
                </tr>
              </tfoot>
            </table>
            <div className="flex space-x-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-400">
                  Title
                </label>
                <input
                  type="text"
                  name="title"
                  value={newSurchargeItem.title}
                  onChange={handleSurchargeChange}
                  placeholder="Surcharge Title"
                  className="mt-1 block w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Cost Code
                </label>
                <Select
                  options={costCodeOptions}
                  value={
                    costCodeOptions.find(
                      (option) => option.value === newSurchargeItem.costCode,
                    ) || null
                  }
                  onChange={(option) =>
                    setNewSurchargeItem((prev) => ({
                      ...prev,
                      costCode: option ? option.value : "",
                    }))
                  }
                  placeholder="Select Cost Code"
                  styles={customStyles}
                  className="w-48 mt-1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Quantity
                </label>
                <input
                  type="number"
                  name="quantity"
                  value={newSurchargeItem.quantity}
                  onChange={handleSurchargeChange}
                  placeholder="Quantity"
                  step="1"
                  className="mt-1 w-20 rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Unit Cost
                </label>
                <input
                  type="number"
                  name="unitCost"
                  value={newSurchargeItem.unitCost}
                  onChange={handleSurchargeChange}
                  placeholder="Unit Cost"
                  step="0.01"
                  className="mt-1 w-20 rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Unit Price
                </label>
                <input
                  type="number"
                  name="unitPrice"
                  value={newSurchargeItem.unitPrice}
                  onChange={handleSurchargeChange}
                  placeholder="Unit Price"
                  step="0.01"
                  className="mt-1 w-20 rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Customer
                </label>
                <input
                  type="checkbox"
                  name="customer"
                  checked={newSurchargeItem.customer}
                  onChange={(e) =>
                    setNewSurchargeItem((prev) => ({
                      ...prev,
                      customer: e.target.checked,
                    }))
                  }
                  className="mt-1 h-4 w-4 text-orange-400 focus:ring-orange-500 border-gray-500 rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Budget
                </label>
                <input
                  type="checkbox"
                  name="budget"
                  checked={newSurchargeItem.budget}
                  onChange={(e) =>
                    setNewSurchargeItem((prev) => ({
                      ...prev,
                      budget: e.target.checked,
                    }))
                  }
                  className="mt-1 h-4 w-4 text-orange-400 focus:ring-orange-500 border-gray-500 rounded"
                />
              </div>
              <div className="flex items-end">
                <button
                  type="button"
                  onClick={handleAddSurchargeItem}
                  className="px-4 py-2 text-sm font-medium text-gray-200 bg-orange-400 rounded-md hover:bg-orange-500 transition-colors duration-200"
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gray-700 p-6 rounded-lg border border-gray-600">
          <h2 className="text-lg font-semibold text-white mb-4">Summary</h2>
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-12">
              <div className="md:col-span-6">
                <label className="block text-sm font-medium text-gray-400">
                  Description
                </label>
                <textarea
                  name="description"
                  value={summary.description}
                  onChange={handleSummaryChange}
                  placeholder="Enter description"
                  className="mt-1 block w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                  rows="4"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-400">
                  Qty/Unit
                </label>
                <input
                  type="text"
                  name="qtyUnit"
                  value={summary.qtyUnit}
                  onChange={handleSummaryChange}
                  placeholder="Qty/Unit"
                  className="mt-1 block w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                />
              </div>
              <div className="md:col-span-4 flex items-center space-x-2">
                <span className="text-sm text-gray-400">Billable</span>
                <button
                  type="button"
                  onClick={handleBillableToggle}
                  className={`relative w-20 h-8 rounded-full flex items-center justify-between px-1 transition-colors ${
                    summary.billable ? "bg-orange-400" : "bg-gray-500"
                  }`}
                >
                  <span className="w-1/2 text-center text-gray-200 text-sm z-10">
                    Yes
                  </span>
                  <span className="w-1/2 text-center text-gray-200 text-sm z-10">
                    No
                  </span>
                  <span
                    className={`absolute w-9 h-6 bg-gray-200 rounded-full top-1 transition-transform ${
                      summary.billable ? "translate-x-9" : "translate-x-0"
                    }`}
                  ></span>
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-12 md:gap-x-2">
              <div className="md:col-span-4">
                <label className="block text-sm font-medium text-gray-400">
                  Total Estimated Cost (Materials + Labor + Surcharges)
                </label>
                <input
                  type="text"
                  value={formatNumber(totalCost)}
                  disabled
                  className="mt-1 block w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm sm:text-sm"
                />
              </div>
              <div className="md:col-span-4">
                <label className="block text-sm font-medium text-gray-400">
                  Markup (%)
                </label>
                <input
                  type="text"
                  value={markup}
                  disabled
                  className="mt-1 block w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm sm:text-sm"
                />
              </div>
              <div className="md:col-span-4">
                <label className="block text-sm font-medium text-gray-400">
                  Total Price for Customer (incl. OH)
                </label>
                <input
                  type="text"
                  value={formatNumber(totalPriceForCustomer)}
                  disabled
                  className="mt-1 block w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm sm:text-sm"
                />
              </div>
            </div>
          </div>
          <div className="flex justify-end space-x-4 mt-4">
            <button
              type="button"
              onClick={() =>
                navigate(`/Projects/${project._id}`, {
                  state: { initialTab: "Change Order (CO)" },
                })
              }
              className="px-4 py-2 text-sm font-medium text-gray-200 bg-gray-600 rounded-md hover:bg-gray-500 transition-colors duration-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className={`px-4 py-2 text-sm font-medium text-white rounded-md ${
                isLoading
                  ? "bg-gray-500 cursor-not-allowed"
                  : "bg-orange-400 hover:bg-orange-500 transition-colors duration-200"
              }`}
            >
              Save Change Order
            </button>
            <button
              type="button"
              onClick={generateChangeOrderPDF}
              disabled={!savedChangeOrder}
              className={`px-4 py-2 text-sm font-medium text-white rounded-md ${
                savedChangeOrder
                  ? "bg-orange-400 hover:bg-orange-500 transition-colors duration-200"
                  : "bg-gray-500 cursor-not-allowed"
              }`}
            >
              Generate PDF
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
